import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import AnimatedBackground from '../../components/ui/AnimatedBackground';
import OrderQueueTable from './components/OrderQueueTable';
import OrderInspector from './components/OrderInspector';
import ExecutionMetrics from './components/ExecutionMetrics';
import QuickActions from './components/QuickActions';
import { astraAPI } from '../../services/astraApi';

/**
 * Trade Execution Management
 *
 * 🔧 PRODUCTION VERSION (2025-10-27): Real Order Data
 *
 * Issues Fixed:
 * 1. Removed 100% hardcoded mock orders (Lines 18-117 in old version)
 * 2. Removed hardcoded executionMetrics (Lines 119-125 in old version)
 * 3. Integrated with /trading/orders API
 * 4. Integrated with /trading/execution/metrics API
 * 5. Added real-time WebSocket updates for orders
 * 6. Added loading and error states
 * 7. Auto-refresh every 10 seconds for active orders
 */

const TradeExecutionManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Real data states
  const [orders, setOrders] = useState([]);
  const [executionMetrics, setExecutionMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    fetchOrdersData();

    // Auto-refresh active orders every 10 seconds
    const interval = setInterval(fetchOrdersData, 10000);

    // Connect WebSocket for real-time updates
    const ws = astraAPI.subscribeToTradingActivity((data) => {
      console.log('📡 Trading activity update:', data);

      if (data.type === 'order_update' || data.type === 'trade') {
        // Update orders in real-time
        setOrders(prev => {
          const index = prev.findIndex(o => o.order_id === data.order_id || o.orderId === data.order_id);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = { ...updated[index], ...data };
            return updated;
          }
          // New order - prepend to list
          return [transformOrder(data), ...prev];
        });

        setWsConnected(true);
      }
    });

    return () => {
      clearInterval(interval);
      if (ws && ws.close) ws.close();
    };
  }, []);

  const fetchOrdersData = async () => {
    try {
      if (!orders || orders.length === 0) {
        setLoading(true);
      }
      setError(null);

      // Fetch real orders and metrics from API
      const ordersData = await astraAPI.getTradingOrders('all', 100);
      const metricsData = await astraAPI.getExecutionMetrics();

      console.log('📊 Trade Execution Data:', {
        ordersCount: ordersData.length,
        metrics: metricsData
      });

      // Transform to component format
      const transformedOrders = ordersData.map(transformOrder);

      setOrders(transformedOrders);
      setExecutionMetrics(metricsData);

      // Set first order as selected by default
      if (transformedOrders.length > 0 && !selectedOrder) {
        setSelectedOrder(transformedOrders[0]);
      }

    } catch (err) {
      console.error('Failed to fetch orders data:', err);
      setError(`Failed to load order data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const transformOrder = (order) => {
    // Transform API order format to component format
    return {
      orderId: order.order_id,
      symbol: order.symbol,
      side: order.side,
      type: order.type,
      quantity: order.quantity,
      targetPrice: order.target_price,
      status: order.status,
      venue: order.venue,
      timestamp: order.timestamp,
      executionTimestamp: order.execution_timestamp,
      executionPrice: order.execution_price,
      filledQuantity: order.filled_quantity,
      executionTime: order.execution_time_seconds ? `${order.execution_time_seconds.toFixed(1)}s` : null,
      slippage: order.slippage,
      aiConfidence: order.ai_confidence ? `${(order.ai_confidence * 100).toFixed(0)}%` : null,
      aiReasoning: order.ai_reasoning,
      aiModel: 'ASTRA-v2.1',
      profitLoss: order.profit_loss
    };
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
  };

  const handleBulkAction = (action, orderIds) => {
    console.log(`Bulk action: ${action} on orders:`, orderIds);
    // TODO: Implement bulk actions API
  };

  const handleOrderUpdate = (updatedOrder) => {
    console.log('Order updated:', updatedOrder);
    // Update in state
    setOrders(prev => {
      const index = prev.findIndex(o => o.orderId === updatedOrder.orderId);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = updatedOrder;
        return updated;
      }
      return prev;
    });
  };

  const handleManualOverride = (orderId) => {
    console.log('Manual override for order:', orderId);
    // TODO: Implement manual override API
  };

  const handleQuickAction = (action, data = null) => {
    console.log('Quick action:', action, data);
    // TODO: Implement quick actions API
  };

  const handleExportData = (type) => {
    console.log('Export data:', type);
    // TODO: Implement export functionality
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchOrdersData();
  };

  const handleExport = (format) => {
    console.log('Exporting data in format:', format);
    handleExportData(format);
  };

  const handleFilter = () => {
    console.log('Applying filters...');
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const staggerChildVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 }
  };

  // Loading state
  if (loading && !executionMetrics) {
    return (
      <AnimatedBackground className="min-h-screen">
        <Header />
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <main className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } mt-16 p-6`}>
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
              <p className="text-lg text-muted-foreground">Loading order data...</p>
            </div>
          </div>
        </main>
      </AnimatedBackground>
    );
  }

  return (
    <AnimatedBackground className="min-h-screen">
      <Header />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <motion.main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } mt-16 p-6`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header Section */}
          <motion.div
            variants={staggerChildVariants}
            transition={{ delay: 0.1 }}
          >
            <Breadcrumb />
            <div className="flex items-center justify-between mb-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className="text-3xl font-bold text-foreground bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Trade Execution Management
                </h1>
                <p className="text-muted-foreground mt-1">
                  Monitor and manage order lifecycle from AI recommendation to settlement
                </p>
              </motion.div>

              {/* Real-time indicator */}
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></div>
                <span className="text-xs text-muted-foreground">
                  {wsConnected ? 'Live Updates' : 'Polling'}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Quick Action Toolbar */}
          <motion.div
            variants={staggerChildVariants}
            transition={{ delay: 0.3 }}
          >
            <QuickActionToolbar
              selectedCount={0}
              onRefresh={handleRefresh}
              onExport={handleExport}
              onFilter={handleFilter}
            />
          </motion.div>

          {/* Error Banner */}
          {error && (
            <motion.div
              variants={staggerChildVariants}
              transition={{ delay: 0.35 }}
              className="bg-error/10 border border-error/20 rounded-lg p-4"
            >
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-error" />
                <p className="text-sm text-error">{error}</p>
                <button
                  onClick={fetchOrdersData}
                  className="ml-auto px-3 py-1 text-xs bg-error text-white rounded hover:bg-error/90"
                >
                  Retry
                </button>
              </div>
            </motion.div>
          )}

          {/* Execution Metrics */}
          {executionMetrics && (
            <motion.div
              variants={staggerChildVariants}
              transition={{ delay: 0.4 }}
            >
              <ExecutionMetrics metrics={executionMetrics} />
            </motion.div>
          )}

          {/* Main Content Layout */}
          <motion.div
            className="grid grid-cols-1 xl:grid-cols-4 gap-6"
            variants={staggerChildVariants}
            transition={{ delay: 0.5 }}
          >
            {/* Left Column - Order Queue and Order Inspector */}
            <div className="xl:col-span-3 space-y-6">
              {/* Order Queue Table */}
              <motion.div
                className="h-96"
                whileHover={{ scale: 1.001 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <OrderQueueTable
                  orders={orders}
                  onOrderSelect={handleOrderSelect}
                  selectedOrderId={selectedOrder?.orderId}
                  onBulkAction={handleBulkAction}
                />
              </motion.div>

              {/* Order Inspector */}
              <motion.div
                className="h-80"
                whileHover={{ scale: 1.001 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <OrderInspector
                  selectedOrder={selectedOrder}
                  onOrderUpdate={handleOrderUpdate}
                  onManualOverride={handleManualOverride}
                />
              </motion.div>
            </div>

            {/* Right Column - Quick Actions */}
            <motion.div
              className="xl:col-span-1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <QuickActions
                onQuickAction={handleQuickAction}
                onExportData={handleExportData}
              />
            </motion.div>
          </motion.div>
        </div>
      </motion.main>
    </AnimatedBackground>
  );
};

export default TradeExecutionManagement;
